import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { usePublic } from "@/pages/public/PublicLayout";
import { fileUrl } from "@/lib/api";
import { allergenIcon } from "@/lib/allergens";
import { ArrowLeft, Filter } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const STATUS_LABEL = { sold_out: { it: "Esaurito", en: "Sold out", fr: "Épuisé", de: "Ausverkauft", es: "Agotado", pt: "Esgotado" } };

const UI = {
  it: { notFound: "Categoria non trovata.", allergens: "Allergeni", hideWith: "Nascondi piatti con:", reset: "Reset", noDishes: "Nessun piatto disponibile." },
  en: { notFound: "Category not found.", allergens: "Allergens", hideWith: "Hide dishes with:", reset: "Reset", noDishes: "No dishes available." },
  fr: { notFound: "Catégorie introuvable.", allergens: "Allergènes", hideWith: "Masquer les plats avec :", reset: "Réinit.", noDishes: "Aucun plat disponible." },
  de: { notFound: "Kategorie nicht gefunden.", allergens: "Allergene", hideWith: "Gerichte ausblenden mit:", reset: "Zurücksetzen", noDishes: "Keine Gerichte verfügbar." },
  es: { notFound: "Categoría no encontrada.", allergens: "Alérgenos", hideWith: "Ocultar platos con:", reset: "Restablecer", noDishes: "Ningún plato disponible." },
  pt: { notFound: "Categoria não encontrada.", allergens: "Alergénios", hideWith: "Ocultar pratos com:", reset: "Repor", noDishes: "Nenhum prato disponível." },
};

const PORTION_LABEL = {
  per_person: { it: "a persona", en: "per person", fr: "par personne", de: "pro Person", es: "por persona", pt: "por pessoa", hr: "po osobi", hu: "személyenként", nl: "per persoon", pl: "od osoby", ro: "de persoană", ru: "за человека", sk: "za osobu", zh: "每人" },
  min_2: { it: "minimo 2 persone", en: "minimum 2 people", fr: "minimum 2 personnes", de: "mindestens 2 Personen", es: "mínimo 2 personas", pt: "mínimo 2 pessoas", hr: "najmanje 2 osobe", hu: "legalább 2 fő", nl: "minimaal 2 personen", pl: "minimum 2 osoby", ro: "minim 2 persoane", ru: "минимум 2 человека", sk: "minimálne 2 osoby", zh: "最少2人" },
};
const portionText = (dish, lang) => {
  const pp = dish.per_person ?? (dish.portion === "per_person");
  const m2 = dish.min_two ?? (dish.portion === "min_2");
  const L = (k) => PORTION_LABEL[k][lang] || PORTION_LABEL[k].it;
  if (pp && m2) return `${L("per_person")} (${L("min_2")})`;
  if (pp) return L("per_person");
  if (m2) return `(${L("min_2")})`;
  return null;
};

const trDish = (dish, lang) => {
  if (lang === "it") return { name: dish.name, description: dish.description };
  const t = dish.translations?.[lang];
  return t ? { name: t.name || dish.name, description: t.description || dish.description } : { name: dish.name, description: dish.description };
};

const DishCard = ({ dish, i, theme, lang, allergens }) => {
  const t = trDish(dish, lang);
  const portion = portionText(dish, lang);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.05 }}
      data-testid={`public-dish-${dish.id}`}
      className="rounded-2xl p-4"
      style={{ backgroundColor: theme.surface, border: `1px solid ${theme.accent}22` }}
    >
      <div className="flex gap-4">
        {dish.image_path && <img src={fileUrl(dish.image_path)} alt={t.name} className="w-24 h-24 rounded-xl object-cover flex-shrink-0" loading="lazy" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h4 className="text-xl leading-tight" style={{ fontFamily: theme.heading, color: theme.text }}>{t.name}</h4>
            {!(dish.formats?.length > 0) && dish.price != null && (
              <span className="text-right whitespace-nowrap" style={{ color: theme.accent }}>
                <span className="font-semibold">€ {dish.price.toFixed(2)}</span>
                {portion && <span className="block text-[11px] font-normal opacity-80" style={{ color: theme.muted }}>{portion}</span>}
              </span>
            )}
          </div>
          {t.description && <p className="text-sm mt-1 leading-relaxed whitespace-pre-line" style={{ color: theme.muted }}>{t.description}</p>}
          {(dish.origin || dish.alcohol != null) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: theme.muted }}>
              {dish.origin && <span>{dish.origin}</span>}
              {dish.alcohol != null && <span>{dish.alcohol}% vol</span>}
            </div>
          )}
          {dish.formats?.length > 0 && (
            <div className="mt-2 space-y-1" data-testid={`dish-formats-${dish.id}`}>
              {dish.formats.map((f, idx) => (
                <div key={idx} className="flex items-baseline justify-between gap-3 pt-1" style={{ borderTop: `1px solid ${theme.accent}1f` }}>
                  <span className="text-sm" style={{ color: theme.text }}>{f.label}</span>
                  <span className="text-sm font-semibold whitespace-nowrap" style={{ color: theme.accent }}>€ {Number(f.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          {dish.status === "sold_out" && (
            <span className="inline-block mt-2 text-xs font-semibold rounded-full px-2.5 py-1" style={{ backgroundColor: theme.bg, color: theme.muted, border: `1px solid ${theme.muted}44` }}>{STATUS_LABEL.sold_out[lang] || STATUS_LABEL.sold_out.it}</span>
          )}
          {dish.allergens?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {dish.allergens.map((code) => {
                const meta = allergens.find((a) => a.code === code);
                const Icon = allergenIcon(code);
                const name = meta ? (lang === "it" ? meta.name_it : meta.name_en) : code;
                return (
                  <Tooltip key={code}>
                    <TooltipTrigger asChild>
                      <span data-testid={`allergen-${code}`} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.accent + "1f", color: theme.accent }}><Icon className="w-3.5 h-3.5" /></span>
                    </TooltipTrigger>
                    <TooltipContent>{name}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const DishList = () => {
  const { data, theme, lang, slug } = usePublic();
  const { menuId, categoryId } = useParams();
  const navigate = useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const tr = UI[lang] || UI.it;

  const menu = (data.menus || []).find((m) => m.id === menuId);
  const cat = menu?.categories.find((c) => c.id === categoryId);
  const allergens = data.allergens;
  if (!cat) return <p style={{ color: theme.muted }} className="text-center py-10">{tr.notFound}</p>;

  const visible = (d) => !d.allergens?.some((a) => excluded.includes(a));
  const parentDishes = (cat.dishes || []).filter(visible);
  const children = (menu.categories || []).filter((c) => c.parent_id === categoryId);
  const catDesc = (lang !== "it" && cat.desc_translations?.[lang]) || cat.description;

  return (
    <TooltipProvider delayDuration={80}>
      <div>
        <button onClick={() => navigate(`/m/${slug}/menu/${menuId}`)} data-testid="dish-back" className="flex items-center gap-1.5 text-sm mb-4" style={{ color: theme.muted }}>
          <ArrowLeft className="w-4 h-4" /> {(lang !== "it" && menu?.translations?.[lang]?.name) || menu?.name}
        </button>
        <div className="flex items-end justify-between mb-2">
          <h2 className="text-3xl" style={{ fontFamily: theme.heading, color: theme.text }}>{(lang !== "it" && cat.translations?.[lang]) || cat.name}</h2>
          <button onClick={() => setFilterOpen(!filterOpen)} data-testid="allergen-filter-toggle" className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ backgroundColor: excluded.length ? theme.accent : theme.surface, color: excluded.length ? theme.bg : theme.muted }}>
            <Filter className="w-3.5 h-3.5" /> {tr.allergens} {excluded.length > 0 && `(${excluded.length})`}
          </button>
        </div>
        {catDesc && <p className="text-sm mb-3 leading-relaxed" style={{ color: theme.muted }} data-testid="cat-description">{catDesc}</p>}
        <div className="h-px w-14 mb-5" style={{ background: `linear-gradient(90deg, ${theme.accent}, transparent)` }} />

        {filterOpen && (
          <div className="rounded-2xl p-4 mb-5" style={{ backgroundColor: theme.surface }} data-testid="allergen-filter-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">{tr.hideWith}</p>
              <button onClick={() => setExcluded([])} className="text-xs" style={{ color: theme.accent }}>{tr.reset}</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allergens.map((a) => {
                const Icon = allergenIcon(a.code);
                const on = excluded.includes(a.code);
                return (
                  <button key={a.code} data-testid={`filter-allergen-${a.code}`} onClick={() => setExcluded(on ? excluded.filter((c) => c !== a.code) : [...excluded, a.code])} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors" style={on ? { backgroundColor: theme.accent, color: theme.bg } : { backgroundColor: theme.accent + "1f", color: theme.text }}>
                    <Icon className="w-3.5 h-3.5" /> {lang === "it" ? a.name_it : a.name_en}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {parentDishes.map((dish, i) => <DishCard key={dish.id} dish={dish} i={i} theme={theme} lang={lang} allergens={allergens} />)}
          {parentDishes.length === 0 && children.length === 0 && <p className="text-center py-10" style={{ color: theme.muted }}>{tr.noDishes}</p>}
        </div>

        {children.map((child) => {
          const childDishes = (child.dishes || []).filter(visible);
          if (childDishes.length === 0) return null;
          const childName = (lang !== "it" && child.translations?.[lang]) || child.name;
          const childDesc = (lang !== "it" && child.desc_translations?.[lang]) || child.description;
          return (
            <div key={child.id} className="mt-8" data-testid={`sub-section-${child.id}`}>
              {child.cover_image ? (
                <div className="glow-hover relative w-full h-28 sm:h-32 md:h-36 rounded-2xl overflow-hidden mb-4" style={{ border: `1px solid ${theme.accent}40`, "--glow": theme.accent, "--glow-soft": `${theme.accent}55` }} data-testid={`sub-banner-${child.id}`}>
                  <img src={fileUrl(child.cover_image)} alt={childName} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.bg}f0 0%, ${theme.bg}55 55%, transparent 100%)` }} />
                  <div className="absolute inset-0 p-4 flex flex-col justify-end">
                    <h3 className="text-2xl leading-tight" style={{ fontFamily: theme.heading, color: theme.text }}>{childName}</h3>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl mb-1" style={{ fontFamily: theme.heading, color: theme.accent }}>{childName}</h3>
                  {childDesc && <p className="text-sm mb-1 leading-relaxed" style={{ color: theme.muted }} data-testid={`sub-description-${child.id}`}>{childDesc}</p>}
                  <div className="h-px w-10 mb-4" style={{ background: `linear-gradient(90deg, ${theme.accent}, transparent)` }} />
                </>
              )}
              {child.cover_image && childDesc && <p className="text-sm mb-4 leading-relaxed" style={{ color: theme.muted }} data-testid={`sub-description-${child.id}`}>{childDesc}</p>}
              <div className="space-y-3">
                {childDishes.map((dish, i) => <DishCard key={dish.id} dish={dish} i={i} theme={theme} lang={lang} allergens={allergens} />)}
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default DishList;
